import type { HtmlAttributes } from '@studio/design-system/props';
import { toUri } from '@studio/models/audio';
import { useDeepEffect, useLatestFn } from '@studio/react-utils/hooks';
import { isNotNil } from '@tryiris-ai/js-types';
import { omit } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import { Content } from './content.jsx';
import { Footer } from './footer.jsx';
import { Panel as _Panel } from '#~/common/editor/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

/** Source `<Panel>` component. */
export const Panel = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const selection = useStore(({ source }) => source.selection);

  // As soon as the candidate has something that resembles an audio URI, we can continue.
  const isReady = useStore((µ) => {
    const config = µ.candidate.audio;

    return config ? isNotNil(toUri(config)) : false;
  });

  const { track } = useAnalytics();

  // Keep the candidate device in sync with the selected discovery.
  const handleSelectionChange = useLatestFn(() => {
    producer((draft) => {
      // Sync candidate with selection.  Don't do anything fancy here.
      draft.candidate = selection ? omit(selection, 'id') : {};

      track(EVENT_NAMES.CHANGE_AUDIO_REFERENCE_SOURCE, EVENT_CATEGORIES.AUDIO_SOURCES, {
        candidate: draft.candidate,
        deviceType: draft.candidate.audio?.protocol?.toString() ?? '',
        brand: draft.candidate.modelId?.toString() ?? '',
        studioId: draft.roomId.toString()
      });
    });
  });

  useDeepEffect(() => {
    handleSelectionChange();
  }, [selection]);

  useEffect(() => {
    producer(({ source }) => {
      source.isReady = isReady;
    });
  }, [isReady]);

  return (
    <_Panel {...props}>
      <Content />
      <Footer />
    </_Panel>
  );
};
